import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Point } from '@os/engine';
import { Menu, pointAnchor } from '../../ui';

export type QuickKind = 'sticky' | 'shape' | 'text';
const KINDS: QuickKind[] = ['sticky', 'shape', 'text'];

/**
 * The menu at the free end of a connector released in the void (the editor fills the window, so its viewport
 * coordinates are client coordinates): picks the linked object to add there. On the ui `Menu` (P16-2): Escape, or a
 * press anywhere else, cancels (the connector disappears).
 */
export function QuickCreateMenu({ at, onPick, onCancel }: { at: Point; onPick: (k: QuickKind) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const anchor = useMemo(() => pointAnchor(at.x + 8, at.y + 8), [at.x, at.y]);
  return (
    <Menu.Root
      open
      modal={false}
      loopFocus
      onOpenChange={(open, details) => {
        // A pick closes the menu too: only the other ways cancel.
        if (!open && details.reason !== 'item-press') onCancel();
      }}
    >
      <Menu.Popup label={t('quickCreate.label')} anchor={anchor} sideOffset={0} finalFocus={false} focusFirst className="min-w-40">
        {KINDS.map((k) => (
          <Menu.Item key={k} onClick={() => onPick(k)}>
            {t(`tools.${k}`)}
          </Menu.Item>
        ))}
      </Menu.Popup>
    </Menu.Root>
  );
}
